import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { BRAND_PRESETS, DEFAULT_BRAND } from '../lib/appearance.ts';

export const swatchStyle = (hex: string) => ({ ['--swatch' as string]: hex }) as CSSProperties;

/**
 * Die Farbliste -- dieselbe in der Kopfleiste (Hausfarbe des Browsers) und im
 * Regie-Pult (Farbe einer Einblendung): sechs Vorgaben und ein freies Feld,
 * im Pult davor noch „Standard". Eine Vorgabe waehlt und schliesst, das freie
 * Feld bleibt offen, solange man zieht.
 */
export function ColorOptions({
  title,
  value,
  standard,
  onPick,
  onCustom,
}: {
  title: string;
  /** Aktuelle Farbe als `#rrggbb`; null heisst „Standard". */
  value: string | null;
  /** Beschriftung einer Wahl „keine eigene Farbe" ganz oben -- ohne sie fehlt die Zeile. */
  standard?: string;
  onPick: (hex: string | null) => void;
  onCustom: (hex: string) => void;
}) {
  const selected =
    value === null ? 'standard' : (BRAND_PRESETS.find((p) => p.hex === value.toLowerCase())?.id ?? 'custom');
  return (
    <>
      <p className="appearance__title">{title}</p>
      {standard && (
        <button
          type="button"
          role="menuitemradio"
          aria-checked={selected === 'standard'}
          className={`appearance__option${selected === 'standard' ? ' is-active' : ''}`}
          onClick={() => onPick(null)}
        >
          <span className="swatch swatch--standard" />
          {standard}
          {selected === 'standard' && <Check />}
        </button>
      )}
      {BRAND_PRESETS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="menuitemradio"
          aria-checked={selected === p.id}
          className={`appearance__option${selected === p.id ? ' is-active' : ''}`}
          onClick={() => onPick(p.hex)}
        >
          <span className="swatch" style={swatchStyle(p.hex)} />
          {p.label}
          {selected === p.id && <Check />}
        </button>
      ))}
      <label className={`appearance__option appearance__custom${selected === 'custom' ? ' is-active' : ''}`}>
        <input type="color" value={value ?? DEFAULT_BRAND} onChange={(e) => onCustom(e.target.value)} />
        Eigene Farbe
        {selected === 'custom' ? <span className="appearance__hex">{value}</span> : null}
      </label>
    </>
  );
}

/**
 * Farbknopf mit aufklappender Liste fuer enge Stellen wie das Regie-Pult. Die
 * Liste schwebt frei ueber der Seite (`position: fixed`) -- das Pult schneidet
 * mit `overflow: hidden` alles ab, was ueber seinen Rand hinausragt. Sie klappt
 * nach oben auf, wenn darunter kein Platz ist, und schliesst beim Scrollen,
 * weil sie sonst vom Knopf wegwandern wuerde.
 */
export function ColorPicker({
  value,
  title,
  label,
  standard,
  onPick,
  onCustom,
}: {
  /** Aktuelle Farbe; null heisst „Standard". */
  value: string | null;
  /** Ueberschrift der Liste. */
  title: string;
  /** Beschriftung des Knopfs fuer Screenreader. */
  label: string;
  standard?: string;
  onPick: (hex: string | null) => void;
  onCustom: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return setPlace(null);
    const button = buttonRef.current?.getBoundingClientRect();
    const menu = menuRef.current?.getBoundingClientRect();
    if (!button || !menu) return;
    const gap = 6;
    const edge = 8;
    let top = button.bottom + gap;
    if (top + menu.height > window.innerHeight - edge) top = Math.max(edge, button.top - gap - menu.height);
    const left = Math.min(Math.max(edge, button.right - menu.width), window.innerWidth - menu.width - edge);
    setPlace({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="color-pick"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={label}
        title={label}
      >
        {value === null ? (
          <span className="swatch swatch--standard" />
        ) : (
          <span className="swatch" style={swatchStyle(value)} />
        )}
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
          <path d="M2 3.5 5 6.5l3-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <>
          <button type="button" className="account__scrim" aria-label="Schließen" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            className="appearance__menu appearance__menu--floating"
            role="menu"
            aria-label={title}
            style={place ? { top: place.top, left: place.left } : { visibility: 'hidden' }}
          >
            <ColorOptions
              title={title}
              value={value}
              standard={standard}
              onPick={(hex) => {
                onPick(hex);
                setOpen(false);
              }}
              onCustom={onCustom}
            />
          </div>
        </>
      )}
    </>
  );
}

function Check() {
  return (
    <svg className="appearance__check" width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      <path d="m3 7.2 2.6 2.6L11 4.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
